import { useCallback, useEffect, useMemo, useState } from "react";
import AdminLayout from "../../components/AdminLayout";
import { listFixcode, patchFixcodeStatus } from "../../api/fixcodeApi";
import { isSuperAdminUser } from "../../utils/brandAuth";
import { readStoredUser } from "../../utils/homeRole";
import "./FixcodeDesk.css";

const FILTERS = [
  { id: "open", label: "Open" },
  { id: "all", label: "All" },
  { id: "deployed", label: "On OVH" },
];

const KIND_LABEL = {
  bug: "Bug",
  issue: "Issue",
  improvement: "Improvement",
};

function isOpen(status) {
  return status !== "dismissed" && status !== "deployed";
}

function when(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function FixcodeDesk() {
  const allowed = isSuperAdminUser(readStoredUser());
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("open");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState("");

  const load = useCallback(async () => {
    if (!allowed) {
      setLoading(false);
      return;
    }
    try {
      const data = await listFixcode();
      setRows(Array.isArray(data) ? data : []);
      setError("");
    } catch (err) {
      setError(err?.message || "Could not load /fixcode messages.");
    } finally {
      setLoading(false);
    }
  }, [allowed]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!allowed) return undefined;
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [allowed, load]);

  const visible = useMemo(() => {
    if (filter === "deployed") return rows.filter((row) => row.status === "deployed");
    if (filter === "open") return rows.filter((row) => isOpen(row.status));
    return rows;
  }, [filter, rows]);

  const queued = rows.filter((row) =>
    ["queued", "claimed", "implementing"].includes(row.status)
  ).length;

  const onStatus = async (requestId, status) => {
    setSavingId(requestId);
    setError("");
    try {
      const updated = await patchFixcodeStatus(requestId, status);
      setRows((prev) =>
        prev.map((row) => (row.request_id === requestId ? { ...row, ...updated } : row))
      );
    } catch (err) {
      setError(err?.message || "Could not update this ticket.");
    } finally {
      setSavingId("");
    }
  };

  return (
    <AdminLayout>
      <div className="fx-root">
        <header className="fx-hero">
          <div>
            <p className="fx-hero__eyebrow">Pingflo · /fixcode</p>
            <h1 className="fx-hero__title">Fix desk</h1>
            <p className="fx-hero__sub">
              Messages sent to Pingflo with <code>/fixcode</code> land here. Pingfix
              implements a queued ticket, then deploys it on OVH.
            </p>
          </div>
          <p className="fx-hero__count">
            <span>{queued}</span>
            with Pingfix
          </p>
        </header>

        {!allowed ? (
          <p className="fx-error">Super admin access is required.</p>
        ) : (
          <>
            <div className="fx-filters" role="tablist" aria-label="Ticket filter">
              {FILTERS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={filter === item.id}
                  className={filter === item.id ? "is-on" : ""}
                  onClick={() => setFilter(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            {error ? <p className="fx-error">{error}</p> : null}
            {loading ? <p className="fx-empty">Loading tickets…</p> : null}
            {!loading && !error && visible.length === 0 ? (
              <p className="fx-empty">
                No tickets in this tray. In Pingflo, send{" "}
                <code>/fixcode bug the score stays at 0</code>.
              </p>
            ) : null}
            <ul className="fx-list">
              {visible.map((row) => {
                const busy = savingId === row.request_id;
                const working = row.status === "claimed" || row.status === "implementing";
                return (
                  <li key={row.request_id} className={`fx-tag is-${row.kind}`}>
                    <div className="fx-tag__punch" aria-hidden="true" />
                    <div className="fx-tag__body">
                      <div className="fx-tag__top">
                        <span className={`fx-stamp is-${row.kind}`}>
                          {KIND_LABEL[row.kind] || row.kind}
                        </span>
                        <span className={`fx-status is-${row.status}`}>{row.status}</span>
                        <span className="fx-id">{row.request_id}</span>
                      </div>
                      <p className="fx-message">{row.message}</p>
                      <p className="fx-meta">
                        {[row.reporter_name, row.reporter_role, row.page_path, when(row.created_at)]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                      {row.agent_note ? <p className="fx-note">{row.agent_note}</p> : null}
                      {row.commit_sha ? (
                        <p className="fx-sha">
                          {row.git_branch} · {row.commit_sha}
                        </p>
                      ) : null}
                      <div className="fx-actions">
                        {row.status !== "queued" && !working ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => onStatus(row.request_id, "queued")}
                          >
                            Send to Pingfix
                          </button>
                        ) : null}
                        {row.status !== "dismissed" && !working ? (
                          <button
                            type="button"
                            className="is-quiet"
                            disabled={busy}
                            onClick={() => onStatus(row.request_id, "dismissed")}
                          >
                            Dismiss
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

export default FixcodeDesk;
